/* eslint-disable no-param-reassign */
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Text,
} from '@cochlear-design-system/foundation';
import { Header } from '../../ui/Header/Header.js';
import { dataMapper } from './dataMapper.js';
import { isValidUrl } from '../../utils/stringFns';
import cookieService from '../../services/cookieService.js';
import {
  USER_COOKIE,
  SHARED_MENU_COOKIE,
  PROVIDER_DETAILS_COOKIE,
  routes,
  ACCESS_ROLES,
} from '../../config';
import providerDetails from '../../actions/providerDetailsAction';
import providerService from '../../services/providerService';
import {
  exchangeProviderToken,
  setIdpParam,
} from '../../utils/authprovider';
import { getLabelsForFields } from '../../utils/labelHelper';
import featureFlagService from '../../services/featureFlagService';
import CustomerContactModal from '../CustomerContactModal';
import useMcpEvents from '../../hooks/useMcpEvents';
import useLiveSessionCheck from '../../hooks/useLiveSessionCheck';
import { openPopup } from '../../utils/popupManager';
import userService from '../../services/userService.js';
import {
  DEFAULT_FLAGS,
  MASHUP_PAGES,
} from '../../constants/constants.js';
import { shouldUseAmplify } from '../../utils/apiTarget';

const HeaderNew = ({
  rendering,
  lang,
  country,
  currentState,
  setCurrentState,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { pathname } = location;
  const providerList =
    providerService.getProviderDetails()?.providerList;
  const currentProvider = providerService.getCurrentProvider();
  const providerCountryCode = currentProvider?.address?.countryCode;
  const providerRegion = providerService.getProviderRegion(
    providerCountryCode,
  );
  const professionalRegion = userService.getUserDetails()?.region;
  const region = providerRegion || professionalRegion;

  const [modalType, setModalType] = useState('contactUs');
  const [openModal, setOpenModal] = useState(false);
  const uDetails = userService.getUserDetails();
  const { role } = uDetails;

  // hook to start postMessage listenter for MCP events
  const { mcpLoaded } = useMcpEvents();
  console.log(
    '[MCP Session Check - PostMessage] MCP loaded',
    mcpLoaded,
  );

  // hook to check livesession cookie from mcp
  const { showButton } = useLiveSessionCheck();
  console.log(
    '[MCP Session Check - LiveSession] Show Button',
    showButton,
  );

  const clinicInAmplifyRegion = shouldUseAmplify(
    currentProvider?.useNewSRPortal ?? false,
  );

  const clinicEquipmentEnabled = !(
    ['LATAM', 'CLASA'].includes(region) && clinicInAmplifyRegion
  );

  useEffect(() => {
    let observer = null;

    if (showButton && !mcpLoaded) {
      console.log(
        '[MCP Session Check] Cleaning up MCP iframes due to non-existent session',
      );

      // First remove all existing MCP iframes
      const removeAllMcpIframes = () => {
        const allIframes = document.querySelectorAll('iframe');
        allIframes.forEach((iframe) => {
          const src = iframe.getAttribute('src') || '';
          if (src.includes(routes.mcp.domain)) {
            if (iframe.parentNode) {
              console.log(
                '[MCP Session Check] Removing MCP iframe:',
                src,
              );
              iframe.parentNode.removeChild(iframe);
            }
          }
        });
      };

      // Remove existing iframes immediately
      removeAllMcpIframes();

      // Set up observer to watch for newly added iframes, for example when navigate to a new page
      observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.addedNodes && mutation.addedNodes.length > 0) {
            for (let i = 0; i < mutation.addedNodes.length; i += 1) {
              const node = mutation.addedNodes[i];

              // Check if the added node is an iframe
              if (node.tagName === 'IFRAME') {
                const src = node.getAttribute('src') || '';
                if (src.includes(routes.mcp.domain)) {
                  console.log(
                    '[MCP Session Check] Removing newly added MCP iframe:',
                    src,
                  );
                  node.parentNode.removeChild(node);
                }
              }

              // Check if the added node contains iframes
              if (node.querySelectorAll) {
                const iframes = node.querySelectorAll('iframe');
                iframes.forEach((iframe) => {
                  const src = iframe.getAttribute('src') || '';
                  if (src.includes(routes.mcp.domain)) {
                    console.log(
                      '[MCP Session Check] Removing nested MCP iframe:',
                      src,
                    );
                    iframe.parentNode.removeChild(iframe);
                  }
                });
              }
            }
          }
        });
      });

      // Start observing the entire document
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    // Clean up observer when component unmounts or conditions change
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [showButton, mcpLoaded]);

  const labels = getLabelsForFields(rendering.fields);

  const canAccessTeamMembers = () => {
    const currentProviderId = currentProvider?.id;
    const accessTokenClaims = userService.getAccessTokenClaims();
    if (
      !currentProviderId ||
      !accessTokenClaims?.actProviderId ||
      !accessTokenClaims?.providerRole
    ) {
      return false;
    }

    return (
      accessTokenClaims?.providerRole ===
      ACCESS_ROLES.PATIENT_MANAGEMENT
    );
  };

  /**
   * Compact menu to store in cookie
   *
   * @param {Array} mn - menu array
   * @returns {Array} - compacted array
   */
  function stripMenu(mn) {
    const compactMenu = [];
    mn.menus.forEach((menu) => {
      compactMenu.push(menu.id);
    });
    return compactMenu;
  }

  function onNavItemClick(selItem) {
    if (selItem.type === 'External') {
      window.open(selItem.url);
    } else if (selItem.type === 'SimpleText') {
      if (selItem.url === '#showContactUsModal') {
        setModalType('contactUs');
        setOpenModal(true);
      } else if (selItem.url === '#showReturnOldPortalModal') {
        setModalType('returnOldPortal');
        setOpenModal(true);
      } else if (isValidUrl(selItem.url)) {
        window.location = selItem.url;
      } else {
        history.push(`/${country}/${lang}/${selItem.url}`);
      }
    }
  }

  const onClinicSelected = async (selItem, professionalId) => {
    setCurrentState({ ...currentState, loading: true });

    // update tokens with new providerId
    const providerToken = await exchangeProviderToken(selItem);
    if (!providerToken) {
      cookieService.removeCookie(PROVIDER_DETAILS_COOKIE);
      window.location.href = routes.dpx.logout;
      return;
    }

    const selectedProvider = providerList?.providers?.find(
      (provider) => provider.id === selItem,
    );
    const defaultFlags = DEFAULT_FLAGS;
    const updatedServiceEnrolments =
      await featureFlagService.getFeatureFlagConfig(
        selItem,
        professionalId,
        selectedProvider?.address?.countryCode,
      );

    const allFlags = {
      featureEnabler: {
        ...defaultFlags.featureEnabler,
        ...updatedServiceEnrolments?.featureEnabler,
      },
    };

    localStorage.setItem('featureFlags', JSON.stringify(allFlags));

    const providerDetailsData = providerService.updateProviderDetails(
      'providerId',
      selItem,
    );

    // update state
    dispatch(providerDetails(providerDetailsData));

    // if we reset the state, then the screen can be already interacted with before the page reloads
    // leave the spinner until the page reloads
    // setCurrentState({...currentState, loading: false});

    // reload page to initialise all screens with new provider details
    // but redirect back to dashboard
    const dest =
      country && lang
        ? `/${country}/${lang}/${routes.dpx.home}`
        : `${routes.dpx.home}`;
    window.location.href = dest;
  };

  const countryConfig = useSelector(
    (state) => state.countryConfig.value,
  );
  const res = dataMapper(
    rendering.fields,
    onNavItemClick,
    lang,
    country,
    countryConfig,
    onClinicSelected,
    labels,
    providerCountryCode,
    region,
    canAccessTeamMembers(),
    clinicEquipmentEnabled,
  );
  cookieService.setCookie(
    SHARED_MENU_COOKIE,
    JSON.stringify(stripMenu(res.config.topNavMenus)),
    262980,
    true,
  );

  cookieService.setCookie(
    USER_COOKIE,
    JSON.stringify({ country, lang }),
    262980,
    true,
  );

  const handleModalClosed = () => {
    setOpenModal(false);
  };

  const shouldShowAlert = () => {
    // Don't show if role is R&T and region is EMEA or APAC
    if (
      role === ACCESS_ROLES.RESOURCES_AND_TRAINING &&
      ['EMEA', 'APAC'].includes(region)
    ) {
      console.log(
        '[MCP Session Check] Not showing alert for R&T in EMEA/APAC',
      );
      return false;
    }

    if (showButton && !mcpLoaded) {
      if (clinicInAmplifyRegion) {
        return false;
      }
      // If it's not a mashup page, don't need to show button
      if (!MASHUP_PAGES.some((page) => pathname.includes(page))) {
        console.log(
          '[MCP Session Check] Not showing alert for non-mashup page',
          pathname,
        );
        return false;
      }
      console.log(
        '[MCP Session Check] Displaying button in',
        pathname,
      );
      return true;
    }
    return false;
  };

  useEffect(() => {
    const handler = (e) => {
      const profileMenuLink = e.target?.closest(
        'a.ccl__my-profile-menu__link',
      );
      if (!profileMenuLink) {
        return;
      }

      const href = profileMenuLink.getAttribute('href');
      if (!href) {
        return;
      }

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) {
          return;
        }
        e.preventDefault();
        history.push(url.pathname + url.search + url.hash);
      } catch (err) {
        e.preventDefault();
        history.push(href);
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [history]);

  return (
    <>
      <Header config={res.config} data={res.data} labels={labels} />
      {shouldShowAlert() && (
        <div className="mcp-session-check-notification">
          <Alert>
            <Text content={labels.sessionNotiMessage} />
            <Button
              text={labels.sessionNotiButton}
              onClick={() =>
                openPopup(
                  setIdpParam(routes.mcp.warmup),
                  'mcp',
                  'popup,width=490,height=280',
                )
              }
            />
          </Alert>
        </div>
      )}

      <CustomerContactModal
        openModal={openModal}
        modalType={modalType}
        labels={labels}
        res={res}
        onCloseClicked={handleModalClosed}
      />
    </>
  );
};

export default HeaderNew;
